import { type Rating, useNodeFeedback, useSubmitNodeFeedback } from './useFeedback';

interface FeedbackButtonsProps {
  nodeId: string;
}

export function FeedbackButtons({ nodeId }: FeedbackButtonsProps) {
  const { data: feedback, isLoading } = useNodeFeedback(nodeId);
  const { mutate: submitFeedback, isPending } = useSubmitNodeFeedback(nodeId);

  const userRating = feedback?.userRating ?? null;
  const isDisabled = isLoading || isPending;

  const handleClick = (rating: Rating) => {
    // Don't submit if already selected (no toggle off)
    if (userRating === rating) return;
    submitFeedback(rating);
  };

  return (
    <div className="flex items-center justify-center gap-4 py-4">
      <span className="text-sm text-reader-text-muted">Rate this chapter:</span>
      <div className="flex gap-2">
        <button
          onClick={() => handleClick('up')}
          disabled={isDisabled}
          aria-label="Thumbs up"
          aria-pressed={userRating === 'up'}
          className={`p-2 rounded-lg transition-all ${
            userRating === 'up'
              ? 'bg-green-600/30 text-green-400 ring-2 ring-green-500/50'
              : 'bg-reader-bg-card text-reader-text-secondary hover:bg-reader-accent-bg hover:text-reader-text-heading'
          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <svg
            className="w-6 h-6"
            fill={userRating === 'up' ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
            />
          </svg>
        </button>
        <button
          onClick={() => handleClick('down')}
          disabled={isDisabled}
          aria-label="Thumbs down"
          aria-pressed={userRating === 'down'}
          className={`p-2 rounded-lg transition-all ${
            userRating === 'down'
              ? 'bg-red-600/30 text-red-400 ring-2 ring-red-500/50'
              : 'bg-reader-bg-card text-reader-text-secondary hover:bg-reader-accent-bg hover:text-reader-text-heading'
          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <svg
            className="w-6 h-6"
            fill={userRating === 'down' ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
